import { EllipsisVertical, GripVertical, X } from 'lucide-react';
import { FieldArray, useFormikContext } from 'formik';
import { uniqueId } from 'lodash';
import React, { useState } from 'react';
import clsx from 'clsx';

import { confirm } from '@dashboard/lib/confirm';

import Button from '@dashboard/components/Button';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import FormElement from '@dashboard/components/Form/components/FormElement';
import Label from '@dashboard/components/Form/components/Label';
import Reorderable from '@dashboard/components/Reorderable';
import { Badge } from '@dashboard/components/ui/badge';

import { Field } from '../Form';
import { renderFields, getIsFieldHidden } from '../formUtils';

function OptionList({
  options = [],
  fields = [],
  allowDelete = true,
  allowAdd = true,
  collapseOptions = false,
  hideIfDisabled = false,
  name,
  onChange,
  onDelete,
  optionShape = {},
  addOptionLabel = '',
  addOptionSize,
  deleteOptionLabel = 'Delete option?',
}) {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [openValue, setOpenValue] = useState('');

  const OPTION = {
    key: uniqueId(),
    ...optionShape,
  };

  return (
    <FormElement>
      <FieldArray
        name={name}
        render={arrayHelpers => {
          const getOptionFields = index =>
            renderFields({
              fields:
                typeof fields === 'function'
                  ? fields({ index, values: values[name][index] })
                  : fields,
              values,
              setFieldValue,
            });

          const renderOptionFields = (index, { hideFirstLabel } = {}) => {
            const optionFields = getOptionFields(index);
            const optionValues = values[name]?.[index] ?? {};

            return optionFields.map((field, i) => {
              const nextField = optionFields
                .slice(i + 1)
                .find(f => f && !getIsFieldHidden(f, optionValues));

              return (
                <Field
                  key={field.name}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  nextFieldType={nextField?.type}
                  field={{
                    ...field,
                    id: `${name}.${index}.${field.name}`,
                    name: `${name}.${index}.${field.name}`,
                    visible: field.visible === undefined ? true : field.visible,
                    hideLabel: (i === 0 && hideFirstLabel) || field.hideLabel,
                    reduceMargin: true,
                    ...(field.fields?.length > 0
                      ? {
                          fields: field.fields.map(subField => ({
                            ...subField,
                            id: `${name}.${index}.${subField.name}`,
                            name: `${name}.${index}.${subField.name}`,
                          })),
                        }
                      : {}),
                  }}
                />
              );
            });
          };

          const handleDeleteOption = async index => {
            const del = await confirm(`${deleteOptionLabel}`, { confirm: 'Delete' });

            if (del) {
              const removedItem = options[index];
              arrayHelpers.remove(index);
              if (onDelete) {
                onDelete(index, removedItem);
              }
            }
          };

          const reorderable = (
            <Reorderable
              id={`fields.${name}`}
              items={options}
              onReorder={items => {
                onChange(items);
              }}
              renderItem={({ dragHandleProps, index, isDragging }) => {
                if (collapseOptions) {
                  const option = options[index];
                  const optionId = option._id || option.key;

                  return (
                    <Reorderable.Item
                      isDragging={isDragging}
                      className={clsx({
                        hidden: hideIfDisabled && !option.enabled,
                      })}
                    >
                      <AccordionItem value={optionId} className="border-b-0">
                        <div className="sticky top-0 bg-white dark:bg-background z-10">
                          <div className="flex flex-row items-center gap-2 px-1">
                            <div
                              {...dragHandleProps}
                              className={clsx(
                                buttonVariants({ variant: 'ghost', size: 'icon' }),
                                'cursor-grab active:cursor-grabbing',
                                'mt-2!',
                              )}
                            >
                              <GripVertical className="size-4" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <AccordionTrigger
                                className="py-3 items-center min-w-0 hover:no-underline"
                                disabled={!option.enabled}
                              >
                                <div className="inline-flex gap-x-2 items-center truncate">
                                  <div className="truncate">
                                    {option.name} <span>{option.required ? '*' : ''}</span>
                                    {option.hidden && (
                                      <Badge variant="secondary" className="ml-2">
                                        Hidden
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </AccordionTrigger>
                            </div>

                            {allowDelete && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <ShadcnButton
                                    variant="ghost"
                                    size="icon"
                                    aria-label="More options"
                                    type="button"
                                  >
                                    <EllipsisVertical className="size-4" />
                                  </ShadcnButton>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onSelect={() => handleDeleteOption(index)}
                                    variant="destructive"
                                  >
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>

                        <AccordionContent className="pb-4 pt-2 relative">
                          <div className="min-w-0">{renderOptionFields(index)}</div>
                        </AccordionContent>
                      </AccordionItem>
                    </Reorderable.Item>
                  );
                }

                const headerField = getOptionFields(index)[0];
                const headerFieldId = headerField
                  ? `${name}.${index}.${headerField.name}`
                  : undefined;

                return (
                  <div
                    className={clsx('flex flex-row items-start gap-2 mb-4', {
                      hidden: hideIfDisabled && !options[index].enabled,
                    })}
                  >
                    <div
                      {...dragHandleProps}
                      className={clsx(
                        buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
                        '-ml-1.5 shrink-0 cursor-grab active:cursor-grabbing mt-1!',
                      )}
                    >
                      <GripVertical className="size-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="mb-1 flex items-end justify-between gap-2">
                        <Label htmlFor={headerFieldId} tooltip={headerField?.tooltip} isInline>
                          {headerField?.label}
                        </Label>

                        {allowDelete && (
                          <ShadcnButton
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Delete option"
                            type="button"
                            className="-mr-1.5 shrink-0"
                            onClick={() => handleDeleteOption(index)}
                          >
                            <X className="size-4" />
                          </ShadcnButton>
                        )}
                      </div>

                      <div>{renderOptionFields(index, { hideFirstLabel: true })}</div>
                    </div>
                  </div>
                );
              }}
            />
          );

          return (
            <>
              {collapseOptions ? (
                <Accordion
                  type="single"
                  collapsible
                  value={openValue}
                  onValueChange={setOpenValue}
                  className="border-t"
                >
                  <Reorderable.Wrapper>{reorderable}</Reorderable.Wrapper>
                </Accordion>
              ) : (
                reorderable
              )}

              {allowAdd && (
                <Button
                  handleClick={() => arrayHelpers.push(OPTION)}
                  appearance="light"
                  size={addOptionSize}
                  type="button"
                >
                  {addOptionLabel}
                </Button>
              )}
            </>
          );
        }}
      />
    </FormElement>
  );
}

export default OptionList;
